import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getIncubation } from '../../controllers/incubations';
import { createParticipant } from '../../controllers/participants';
import { keyToDisplayString, displayStringToKey } from '../../utils/StringUtils';
import Button from '../../components/ui/Button/Button';
import Select from '../../components/ui/Select/Select';
import Spinner from '../../components/ui/Spinner/Spinner';
import { useToast } from '../../contexts/ToastContext';
import './AddParticipantsCsvScreen.css';

const AddParticipantsCsvScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showWarning } = useToast();
  const [incubation, setIncubation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [fieldMappings, setFieldMappings] = useState({});
  const [showFieldMapping, setShowFieldMapping] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  const fileInputRef = useRef(null);

  // Get incubation ID from URL params
  const incubationId = location.state?.incubationId;

  useEffect(() => {
    if (incubationId) {
      fetchIncubationData();
    } else {
      console.error('No incubation ID provided');
      navigate('/home');
    }
  }, [incubationId]);

  const fetchIncubationData = async () => {
    try {
      setLoading(true);
      const response = await getIncubation({ incubation_id: incubationId });
      
      if (response.data && response.data.success) {
        const incubationData = response.data.incubation;
        setIncubation(incubationData);
      } else {
        console.error('Failed to fetch incubation data:', response.data?.error);
        showError('Failed to load incubation data');
        navigate('/home');
      }
    } catch (error) {
      console.error('Error fetching incubation data:', error);
      showError('Error loading incubation data');
      navigate('/home');
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    if (incubationId) {
      navigate('/incubation', { state: { incubationId: incubationId } });
    } else {
      navigate('/home');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        try {
          const { headers, data } = await parseCSV(file);
          setCsvHeaders(headers);
          setCsvData(data);
          // Initialize field mappings with empty values
          const initialMappings = {};
          headers.forEach(header => {
            initialMappings[header] = '';
          });
          setFieldMappings(initialMappings);
        } catch (error) {
          console.error('Error parsing CSV:', error);
          showError('Error reading CSV file');
        }
      } else {
        showWarning('Please select a CSV file');
      }
    }
  };

  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim() !== '');
          const headers = lines[0].split(',').map(header => header.trim());
          const data = lines.slice(1).map(line => {
            const values = line.split(',').map(value => value.trim());
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            return row;
          });
          resolve({ headers, data });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        try {
          const { headers, data } = await parseCSV(file);
          setCsvHeaders(headers);
          setCsvData(data);
          // Initialize field mappings with empty values
          const initialMappings = {};
          headers.forEach(header => {
            initialMappings[header] = '';
          });
          setFieldMappings(initialMappings);
        } catch (error) {
          console.error('Error parsing CSV:', error);
          showError('Error reading CSV file');
        }
      } else {
        showWarning('Please select a CSV file');
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setCsvHeaders([]);
    setCsvData([]);
    setFieldMappings({});
    setShowFieldMapping(false);
    setProcessingResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFieldMappingChange = (csvHeader, dataField) => {
    setFieldMappings(prev => ({
      ...prev,
      [csvHeader]: dataField
    }));
  };

  const handleNextClick = () => {
    setShowFieldMapping(true);
  };

  const getAvailableDataFields = () => {
    if (!incubation) return [];
    
    const dataFields = [];
    
    // Add data fields from incubation - use display_name if available
    if (incubation.data_fields) {
      incubation.data_fields.forEach(field => {
        const displayName = field.display_name || keyToDisplayString(field.name);
        dataFields.push({
          value: `data_${displayName}`,
          label: displayName,
          type: 'data_field',
          fieldType: field.type
        });
      });
    }
    
    // Add inclusion criteria fields
    if (incubation.inclusion_criteria) {
      incubation.inclusion_criteria.forEach(criteria => {
        dataFields.push({
          value: `criteria_${criteria.name.toLowerCase().replace(/\s+/g, '_')}`,
          label: criteria.name,
          type: 'criteria_field',
          fieldType: 'boolean'
        });
      });
    }
    
    // Add exclusion criteria fields
    if (incubation.exclusion_criteria) {
      incubation.exclusion_criteria.forEach(criteria => {
        dataFields.push({
          value: `exclusion_${criteria.name.toLowerCase().replace(/\s+/g, '_')}`,
          label: criteria.name,
          type: 'exclusion_field',
          fieldType: 'boolean'
        });
      });
    }
    
    // Note: Removed basic pet fields to avoid conflicts with data fields
    // The data fields from incubation should be used instead
    const allFields = [...dataFields];
    
    console.log('====== FIELDS ======');
    console.log('Available data fields:', allFields);
    console.log('Incubation data_fields:', incubation.data_fields);
    console.log('Incubation inclusion_criteria:', incubation.inclusion_criteria);
    console.log('===================');
    
    return allFields;
  };

  const convertValue = (value, fieldType) => {
    if (!value || value.trim() === '') {
      return fieldType === 'boolean' ? false : '';
    }

    switch (fieldType) {
      case 'number':
        const num = parseFloat(value);
        return isNaN(num) ? '' : num;
      case 'boolean':
        const boolValue = value.toLowerCase().trim();
        return boolValue === 'true' || boolValue === '1' || boolValue === 'yes' || boolValue === 'y';
      case 'date':
        // Keep as string for date fields
        return value.trim();
      default:
        return value.trim();
    }
  };

  const processCSVData = () => {
    const participants = [];
    
    // Process each row of CSV data to create individual participants
    csvData.forEach((row, rowIndex) => {
      const dataFields = {};
      const inclusionCriteria = {};
      const exclusionCriteria = {};
      
      Object.keys(fieldMappings).forEach(csvHeader => {
        const mappedField = fieldMappings[csvHeader];
        const csvValue = row[csvHeader] || '';
        
        if (mappedField) {
          const availableFields = getAvailableDataFields();
          const fieldInfo = availableFields.find(f => f.value === mappedField);
          const fieldType = fieldInfo?.fieldType || 'text';
          const convertedValue = convertValue(csvValue, fieldType);
          
          if (mappedField.startsWith('data_')) {
            const displayName = mappedField.replace('data_', '');
            const fieldKey = displayStringToKey(displayName); // Convert display name back to key
            dataFields[fieldKey] = convertedValue;
          } else if (mappedField.startsWith('criteria_')) {
            const criteriaName = mappedField.replace('criteria_', '');
            inclusionCriteria[criteriaName] = convertedValue;
          } else if (mappedField.startsWith('exclusion_')) {
            const criteriaName = mappedField.replace('exclusion_', '');
            exclusionCriteria[criteriaName] = convertedValue;
          }
        }
      });
      
      // Create participant data for this row
      participants.push({
        incubation_id: incubationId,
        data_fields: dataFields,
        inclusion_criteria: inclusionCriteria,
        exclusion_criteria: exclusionCriteria,
        row_number: rowIndex + 1 // For error reporting
      });
    });
    
    return participants;
  };

  const handleCompleteMapping = async () => {
    try {
      setProcessing(true);
      setProcessingResults(null);
      
      
      const participants = processCSVData();
      const results = {
        successful: [],
        failed: []
      };
      
      console.log(`Processing ${participants.length} participants from CSV...`);
      
      // Process each participant individually
      for (let i = 0; i < participants.length; i++) {
        const participantData = participants[i];
        
        try {
          console.log(`Creating participant ${i + 1}/${participants.length}:`, participantData);
          
          const response = await createParticipant(participantData);
          
          if (response.data && response.data.success) {
            results.successful.push({
              row: participantData.row_number,
              participantId: response.data.participant?.id
            });
          } else {
            results.failed.push({
              row: participantData.row_number,
              error: response.data?.error || 'Unknown error'
            });
          }
        } catch (error) {
          console.error(`Error creating participant for row ${participantData.row_number}:`, error);
          results.failed.push({
            row: participantData.row_number,
            error: error.message
          });
        }
      }
      
      // Set results based on success/failure
      if (results.failed.length === 0) {
        // All participants created successfully - navigate back to incubation
        navigate('/incubation', { 
          state: { 
            incubationId: incubationId, 
            refreshParticipants: true,
            successMessage: 'Participants from the CSV have been added to this incubation.'
          } 
        });
        return;
      } else if (results.successful.length === 0) {
        setProcessingResults({
          success: false,
          message: `Failed to create any participants. ${results.failed.length} errors occurred.`,
          results: results
        });
      } else {
        setProcessingResults({
          success: true,
          message: `Created ${results.successful.length} participants successfully. ${results.failed.length} failed.`,
          results: results
        });
      }
    } catch (error) {
      console.error('Error processing CSV:', error);
      setProcessingResults({
        success: false,
        message: 'Error processing CSV data. Please try again.',
        error: error.message
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="add-participants-csv-screen">
        <div className="add-participants-csv-header-container">
          <div className="add-participants-csv-header-left">
            <button className="add-participants-csv-back-button" onClick={handleBackClick}>
              <i className="fas fa-arrow-left"></i>
              <span>Back</span>
            </button>
            <div className="add-participants-csv-header">
              <h1 className="add-participants-csv-title">Add Participants via CSV</h1>
            </div>
          </div>
        </div>
        
        <div className="add-participants-csv-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spinner full size="lg" label="Loading incubation data..." />
          </div>
        </div>
      </div>
    );
  }

  if (!incubation) {
    return (
      <div className="add-participants-csv-screen">
        <div className="add-participants-csv-header-container">
          <div className="add-participants-csv-header-left">
            <button className="add-participants-csv-back-button" onClick={handleBackClick}>
              <i className="fas fa-arrow-left"></i>
              <span>Back</span>
            </button>
            <div className="add-participants-csv-header">
              <h1 className="add-participants-csv-title">Add Participants via CSV</h1>
            </div>
          </div>
        </div>
        
        <div className="add-participants-csv-content">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Incubation not found</p>
            <Button variant="primary" onClick={() => navigate('/home')}>
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-participants-csv-screen">
      <div className="add-participants-csv-header-container">
        <div className="add-participants-csv-header-left">
          <button className="add-participants-csv-back-button" onClick={handleBackClick}>
            <i className="fas fa-arrow-left"></i>
            <span>Back</span>
          </button>
          <div className="add-participants-csv-header">
            <h1 className="add-participants-csv-title">Add Participants via CSV</h1>
          </div>
        </div>
      </div>
      
      <div className="add-participants-csv-content">
        <div className="csv-upload-container">
          <div className="csv-upload-instructions">
            <h2>Upload Participants CSV</h2>
            <p>Upload a CSV file containing participant information to add them to this incubation.</p>
          </div>
          
          <div 
            className={`csv-upload-area ${isDragOver ? 'drag-over' : ''} ${selectedFile ? 'file-selected' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleUploadClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            {selectedFile ? (
              <div className="file-selected-content">
                <i className="fas fa-file-csv"></i>
                <div className="file-info">
                  <p className="file-name">{selectedFile.name}</p>
                  <p className="file-size">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                </div>
                <button 
                  className="remove-file-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                >
                  <i className="fas fa-times-circle"></i>
                </button>
              </div>
            ) : (
              <div className="upload-placeholder">
                <i className="fas fa-cloud-upload-alt"></i>
                <h3>Drag CSV file here or click to upload</h3>
                <p>Only CSV files are supported</p>
              </div>
            )}
          </div>
          
          {selectedFile && !showFieldMapping && (
            <div className="csv-upload-actions">
              <Button 
                variant="primary" 
                onClick={handleNextClick}
              >
                Next
                <i className="fas fa-arrow-right"></i>
              </Button>
            </div>
          )}

          {showFieldMapping && (
            <div className="field-mapping-container">
              <div className="field-mapping-header">
                <h3>Map CSV Columns to Data Fields</h3>
                <p>Match each CSV column to the appropriate data field in the system.</p>
              </div>
              
              <div className="field-mapping-rows">
                {csvHeaders.map((header, index) => (
                  <div key={index} className="field-mapping-row">
                    <div className="csv-header">
                      <span className="header-label">{header}</span>
                    </div>
                    <div className="mapping-arrow">
                      <i className="fas fa-arrow-right"></i>
                    </div>
                    <div className="data-field-select">
                      <Select
                        value={fieldMappings[header] || ''}
                        onChange={(e) => handleFieldMappingChange(header, e.target.value)}
                        options={[
                          { value: '', label: 'Select a field...' },
                          ...getAvailableDataFields()
                        ]}
                        placeholder="Select a field..."
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="field-mapping-actions">
                <Button 
                  variant="secondary" 
                  onClick={() => setShowFieldMapping(false)}
                >
                  <i className="fas fa-arrow-left"></i>
                  Back
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleCompleteMapping}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Spinner size="sm" className="inline-spinner" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check"></i>
                      Complete Mapping
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {processingResults && (
            <div className="processing-results">
              <div className={`result-message ${processingResults.success ? 'success' : 'error'}`}>
                <i className={`fas ${processingResults.success ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                <p>{processingResults.message}</p>
                
                {processingResults.results && (
                  <div className="detailed-results">
                    {processingResults.results.successful.length > 0 && (
                      <div className="success-details">
                        <h4>✅ Successful ({processingResults.results.successful.length})</h4>
                        <ul>
                          {processingResults.results.successful.map((result, index) => (
                            <li key={index}>Row {result.row}: Participant created</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {processingResults.results.failed.length > 0 && (
                      <div className="error-details">
                        <h4>❌ Failed ({processingResults.results.failed.length})</h4>
                        <ul>
                          {processingResults.results.failed.map((result, index) => (
                            <li key={index}>Row {result.row}: {result.error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="result-actions">
                  <Button 
                    variant="primary" 
                    onClick={() => navigate('/incubation', { state: { incubationId: incubationId, refreshParticipants: true } })}
                  >
                    <i className="fas fa-eye"></i>
                    View Incubation
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={() => {
                      setProcessingResults(null);
                      setShowFieldMapping(false);
                      setSelectedFile(null);
                      setCsvHeaders([]);
                      setCsvData([]);
                      setFieldMappings({});
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                  >
                    <i className="fas fa-plus"></i>
                    Upload Another CSV
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddParticipantsCsvScreen;
